import { useEffect } from "react";

import { useTranslation } from "react-i18next";

import PropTypes from "prop-types";

import Grid from "@mui/material/Grid";

import { useAsync } from "../../../hooks/useAsync";
import useEquipmentService from "../../../services/equipmentService";
import useItemService from "../../../services/itemService";

import LoaderWrapper from "../../wrapper/LoaderWrapper/LoaderWrapper";

import MeterParametersForm from "./MeterParametersForm";
import MeterLocationForm from "./MeterLocationForm";
import MeterCommunicationLogsForm from "./MeterCommunicationLogsForm";
import MeterAtributesForm from "./MeterAtributesForm";
import MeterTenantForm from "./MeterTenantForm";
import MeterTicketForm from "./MeterTicketForm";
import MeterSchemeForm from "./MeterSchemeForm";
import MeterMeasuringLocationForm from "./MeterMeasuringLocationForm";
import MeterReadingHistoryForm from "./MeterReadingHistoryForm";
import MeterBasicInfoForm from "./MeterBasicInfoForm";
import MeterDescriptionForm from "./MeterDescriptionForm";
import MeterEnclosureForm from "./MeterEnclosuresForm";
import ElectricityDistributionForm from "./ElectricityDistributionForm";
import EquipmentUsageForm from "./EquipmentUsageForm";

import CounterAssignDataDialog from "../../dialog/CounterAssignDataDialog";

export default function MeterPassportForm(props) {
  const { t } = useTranslation();

  const { getEquipmentForMeterDetails } = useEquipmentService();
  const equipmentDetails = useAsync(() =>
    getEquipmentForMeterDetails(props.equipmentId)
  );

  useEffect(() => {
    if (equipmentDetails.loading) return;

    if (props.setIsReadOnly) {
      props.setIsReadOnly(equipmentDetails.value?.equipment_is_readonly);
    }
  }, [equipmentDetails.loading]);

  const { getParametersListForMeters } = useItemService();
  const parameterList = useAsync(() =>
    getParametersListForMeters({ equipment: props.equipmentId })
  );

  const handleSubmit = () => {
    if (props.setExistsChanges) {
      props.setExistsChanges(true);
    }
    if (props.onSubmit) {
      props.onSubmit();
    } else {
      equipmentDetails.refetch();
    }
  };

  const isForElectricalEnergy =
    equipmentDetails.value?.is_for_electrical_energy;

  const isLoading = equipmentDetails.loading || parameterList.loading;

  if (isLoading) return <LoaderWrapper showLoader={true} />;

  return (
    <Grid
      container
      direction="row"
      justifyContent="center"
      alignItems="stretch"
      spacing={1}
      marginBottom={2}
    >
      <Grid item xs={5}>
        <MeterBasicInfoForm formValue={equipmentDetails.value} />
      </Grid>
      <Grid item xs={7}>
        <MeterParametersForm
          equipmentId={equipmentDetails.value.id}
          parameterList={parameterList.value}
          parametersValues={equipmentDetails.value.parameters}
          onSubmit={handleSubmit}
        />
      </Grid>
      <Grid container item xs={7}>
        <Grid item xs={12}>
          <MeterLocationForm
            equipmentId={equipmentDetails.value.id}
            location={equipmentDetails.value.instalation_location}
            lastInstalation={equipmentDetails.value.equipment_date_instalation}
            lastDeinstalation={
              equipmentDetails.value.equipment_date_deinstalation
            }
            onSubmit={handleSubmit}
            readOnly={props.readOnly}
          />
        </Grid>
        <Grid item xs={12}>
          <MeterMeasuringLocationForm
            locations={equipmentDetails.value.measuring_location}
            factors={equipmentDetails.value.measuring_location_factors}
            startSettlement={equipmentDetails.value.eios_start_settlement}
            equipmentId={equipmentDetails.value.id}
            onSubmit={handleSubmit}
            readOnly={props.readOnly}
          />
        </Grid>
        <Grid item xs={12}>
          <MeterTenantForm
            tenant={equipmentDetails.value.tenant}
            startSettlement={equipmentDetails.value.eits_start_settlement}
            equipmentId={equipmentDetails.value.id}
            equipmentTenantSetId={
              equipmentDetails.value.equipment_instalation_tenant_set_id
            }
            power={equipmentDetails.value.eits_power}
            tariffGroup={equipmentDetails.value.electricity_tariff_group}
            ppe={equipmentDetails.value.ppe}
            onSubmit={handleSubmit}
            readOnly={props.readOnly}
          />
        </Grid>
      </Grid>
      <Grid item xs={5}>
        <MeterCommunicationLogsForm
          equipmentId={equipmentDetails.value.id}
          readOnly={false}
        />
      </Grid>
      <Grid item xs={12} marginTop={2}>
        <MeterAtributesForm
          equipmentId={equipmentDetails.value.id}
          formValue={equipmentDetails.value}
          onSubmit={handleSubmit}
        />
      </Grid>
      <Grid container item xs={6} marginTop={2}>
        <Grid item xs={12}>
          <MeterDescriptionForm formValue={equipmentDetails.value} />
        </Grid>
        <Grid item xs={isForElectricalEnergy ? 2.4 : 3}>
          <MeterSchemeForm
            equipmentId={equipmentDetails.value.id}
            isForElectricalEnergy={isForElectricalEnergy}
          />
        </Grid>
        <Grid item xs={isForElectricalEnergy ? 2.4 : 3}>
          <MeterReadingHistoryForm equipmentId={equipmentDetails.value.id} />
        </Grid>
        <Grid item xs={isForElectricalEnergy ? 2.4 : 3}>
          <MeterTicketForm equipmentId={equipmentDetails.value.id} />
        </Grid>
        <Grid item xs={isForElectricalEnergy ? 2.4 : 3}>
          <EquipmentUsageForm equipmentId={equipmentDetails.value.id} />
        </Grid>
        {isForElectricalEnergy && (
          <Grid item xs={2.4}>
            <ElectricityDistributionForm
              equipmentId={equipmentDetails.value.id}
            />
          </Grid>
        )}
      </Grid>
      <Grid item xs={6} marginTop={2}>
        <MeterEnclosureForm
          equipmentId={equipmentDetails.value.id}
          readOnly={false}
        />
      </Grid>
      {props.openAssignDataMeterDialog && (
        <CounterAssignDataDialog
          dialogTitle={t("dialog.equipment.edit_counter_data")}
          open={props.openAssignDataMeterDialog}
          onClose={props.onCloseMeterAssignDataDialog}
          onSubmitCallback={handleSubmit}
          equipmentId={props.equipmentId}
        />
      )}
    </Grid>
  );
}

MeterPassportForm.propTypes = {
  equipmentId: PropTypes.string,
  setExistsChanges: PropTypes.func,
  setIsReadOnly: PropTypes.func,
  onSubmit: PropTypes.func,
  readOnly: PropTypes.bool,
};
